/**
 * Batch Processes Detail State
 *
 * Manages the single process detail view with resolved reference data
 * (publication names, warehouse names, channel names).
 */

import { BatchProcessesReadmodel } from '@bett3r-dev/teselly-domain';
import {
  progressPercentage as progressPercentagePure,
  isFinalizing as isFinalizingPure,
  successCount as successCountPure,
  errorCount as errorCountPure
} from '../../shared/state/progressSelectors';
import { firstRevokedChannelTarget } from '../../shared/state/channelTargets';

export type ResolvedPublication = {
  id: string;
  title: string;
  thumbnail?: string;
  sku?: string;
};

export type ResolvedWarehouse = {
  id: string;
  name: string;
};

export type ResolvedChannel = {
  connectorId: string;
  name: string;
  icon?: string;
};

export type BatchProcessesDetailState = {
  /** The batch process being viewed */
  process: BatchProcessesReadmodel | null;

  /** Resolved publication reference data keyed by ID */
  resolvedPublications: Record<string, ResolvedPublication>;

  /** Resolved warehouse reference data keyed by ID */
  resolvedWarehouses: Record<string, ResolvedWarehouse>;

  /** Resolved channel reference data keyed by connectorId */
  resolvedChannels: Record<string, ResolvedChannel>;

  /**
   * Projected success count derived from the new `batch-progress` readmodel.
   * Counts rows already visible in the user-facing readmodel for this batch.
   * `null` indicates the count has not been loaded yet — selectors fall back
   * to the existing readmodel `completedCount - errorCount` to avoid showing 0%
   * during the first roundtrip.
   */
  projectedSuccessCount: number | null;

  /**
   * TV1-2446 slice 6 — the batch's channel targets (`connectorId_integrationUserId`) whose
   * credential is revoked. Non-empty disables Retry (the server would refuse it). Loaded once per
   * process by `revokedChannelTargetsPolicy`; empty on a read error (fail-open, like the server).
   */
  revokedChannelTargets: string[];

  /** Loading states */
  loading: {
    process: boolean;
    referenceData: boolean;
    projectedSuccessCount: boolean;
  };
};

export const initialState: BatchProcessesDetailState = {
  process: null,
  resolvedPublications: {},
  resolvedWarehouses: {},
  resolvedChannels: {},
  projectedSuccessCount: null,
  revokedChannelTargets: [],
  loading: {
    process: true,
    referenceData: false,
    projectedSuccessCount: false
  }
};

/**
 * Derived state selectors
 */
export const selectors = {
  /**
   * Number of errored publications. Delegates to the lifted pure selector in
   * `batchProcesses/shared` (TV1-1979).
   */
  errorCount: ( state: BatchProcessesDetailState ): number =>
    errorCountPure( state.process ),

  /**
   * Successfully visible items, derived from the new `batch-progress` readmodel.
   * Delegates to the lifted pure selector in `batchProcesses/shared` (TV1-1979).
   */
  successCount: ( state: BatchProcessesDetailState ): number =>
    successCountPure( state.process, state.projectedSuccessCount ),

  /**
   * Progress percentage (0-100). Delegates to the lifted pure selector in
   * `batchProcesses/shared` (TV1-1979), which factors `noOpCount` into
   * `effectiveTotal`.
   */
  progressPercentage: ( state: BatchProcessesDetailState ): number =>
    progressPercentagePure( state.process, state.projectedSuccessCount ),

  /**
   * "Finalizing" affordance. Delegates to the lifted pure selector in
   * `batchProcesses/shared` (TV1-1979).
   */
  isFinalizing: ( state: BatchProcessesDetailState ): boolean =>
    isFinalizingPure( state.process, state.projectedSuccessCount ),

  /**
   * TV1-2446 slice 6 — the first channel target OF THE CURRENT PROCESS found in
   * `revokedChannelTargets`, or `null`. Retry is disabled while it is set, and the Reconnect call
   * to action points at it. Derived from `state.process` so a list left over from the previously
   * viewed process (the store outlives in-app navigation) can never disable an unrelated batch.
   */
  revokedRetryTarget: ( state: BatchProcessesDetailState ): string | null =>
    firstRevokedChannelTarget( state.process, state.revokedChannelTargets )
};
